/**
 * V68: 인력 관리 페이지 접근 가드
 *
 * 허용: system_admin / role_admin_kr / role_admin_cn / role_manager / role_exec
 * 불허: role_hr / 일반 직원 (role_te / role_manager 외)
 *
 * MmAccessGuard 패턴 참고. role_hr 명시 제외.
 */
import React, { useMemo } from 'react';
import { useAuthorization } from '../shared/hooks/useAuthorization';

interface Props {
  children: React.ReactElement;
}

const WORKFORCE_ALLOWED_ROLES = [
  'system_admin',
  'role_admin_kr',
  'role_admin_cn',
  'role_manager',
  'role_exec',
] as const;

export function WorkforceAccessGuard({ children }: Props) {
  const { user, hasRole, isLoading } = useAuthorization();

  const canAccess = useMemo(() => {
    if (isLoading || !user) return null;
    // userType === 'admin' 이면 통과
    if ((user?.profile as any)?.userType === 'admin') return true;
    return WORKFORCE_ALLOWED_ROLES.some(r => hasRole(r));
  }, [user, hasRole, isLoading]);

  if (isLoading || canAccess === null) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '200px', color: 'var(--color-gray-500)',
      }}>
        권한 확인 중...
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-gray-800)', marginBottom: '12px' }}>
          접근 권한이 없습니다
        </h2>
        <p style={{ color: 'var(--color-gray-600)', marginBottom: '8px' }}>
          인력 관리 페이지는 조직장(role_manager) / 임원(role_exec) / 관리자만 접근할 수 있습니다.
        </p>
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '8px 24px', background: 'var(--color-primary)', color: 'white',
              border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
            }}
          >
            뒤로 가기
          </button>
        </div>
      </div>
    );
  }

  return children;
}
